'use client';

import { c as _c } from "react/compiler-runtime";
import { FactiiiGoalType, FactiiiRequirement, type Goal, type NewGoal } from '@shared/all/domains/factiiis';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Confirmation from '../common/Confirmation';
import Input from '../UI/Input';
type Props = {
  goal: NewGoal;
  setNewGoals: (newGoals: NewGoal[]) => void;
  newGoals: NewGoal[];
  setButtonLoading: Function;
};
const GoalItem = props => {
  const $ = _c(30);
  const queryClient = useQueryClient();
  const queryKey = ["goals"];
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  let t0;
  if ($[0] !== props.goal.factiiiId) {
    t0 = g => g.factiiiId === props.goal.factiiiId;
    $[0] = props.goal.factiiiId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selectedTypes = props.newGoals.filter(t0).map(_temp);
  const isGoalPresentInQueryClient = goalId => {
    const data = queryClient.getQueryData(queryKey);
    if (!data) {
      return false;
    }
    return data.pages.some(page => page.goals.some(goal => goal.id === goalId));
  };
  const handleDelete = async () => {
    const [error] = await client.factiiis.deleteGoal.mutate({
      goalId: props.goal.id
    });
    if (!error) {
      props.setNewGoals(props.newGoals.filter(g_1 => g_1.id !== props.goal.id));
      queryClient.setQueryData(queryKey, oldData => {
        if (!oldData) {
          return;
        }
        return {
          pages: oldData.pages.map(page_0 => ({
            ...page_0,
            goals: props.newGoals.filter(g_2 => g_2.id !== props.goal.id)
          })),
          pageParams: oldData.pageParams
        };
      });
    }
    setOpenConfirmationModal(false);
  };
  const validateInput = _temp2;
  const isAdded = isGoalPresentInQueryClient(props.goal.id);
  let t1;
  if ($[2] !== props.goal.name) {
    t1 = <div>{props.goal.name}</div>;
    $[2] = props.goal.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = props.goal.avatarKey ?? null;
  let t3;
  if ($[4] !== t2) {
    t3 = <Avatar spaceAvatarKey={t2} size={40} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = props.goal.data;
  let t5;
  if ($[6] !== props.goal) {
    t5 = validateInput(props.goal);
    $[6] = props.goal;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = !t5.isValid;
  let t7;
  if ($[8] !== props) {
    t7 = e => {
      props.setNewGoals(props.newGoals.map(g_3 => {
        const validation = validateInput(g_3);
        props.setButtonLoading(!validation.isValid);
        if (g_3.id === props.goal.id && g_3.type === props.goal.type) {
          const updatedGoal = {
            ...g_3,
            data: e,
            isValid: validation.isValid,
            error: validation.error
          };
          return updatedGoal;
        }
        return g_3;
      }));
    };
    $[8] = props;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== props.goal.data || $[11] !== t6 || $[12] !== t7) {
    t8 = <Input value={t4} label="Enter goal here..." warning={t6} onChange={t7} />;
    $[10] = props.goal.data;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = props.goal.type;
  let t10;
  if ($[14] !== props) {
    t10 = e_0 => {
      props.setNewGoals(props.newGoals.map(g_4 => {
        if (g_4.id === props.goal.id) {
          return {
            ...g_4,
            type: e_0.target.value as FactiiiGoalType
          };
        }
        return g_4;
      }));
    };
    $[14] = props;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = selectedTypes.includes(FactiiiGoalType.YEARLY);
  let t12;
  if ($[16] !== t11) {
    t12 = <option disabled={t11} value={FactiiiGoalType.YEARLY}>Yearly</option>;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = isAdded ? "Delete" : "Remove Goal";
  let t14;
  if ($[18] !== isAdded || $[19] !== props || $[20] !== setOpenConfirmationModal) {
    t14 = e_1 => {
      if (isAdded) {
        return setOpenConfirmationModal(true);
      }
      e_1.stopPropagation();
      props.setNewGoals(props.newGoals.filter(g_5 => g_5.id !== props.goal.id));
    };
    $[18] = isAdded;
    $[19] = props;
    $[20] = setOpenConfirmationModal;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <X size={20} />;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== t13 || $[24] !== t14) {
    t16 = <button title={t13} className="rounded-md p-3 hover:bg-background-light" onClick={t14}>{t15}</button>;
    $[23] = t13;
    $[24] = t14;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== handleDelete || $[27] !== openConfirmationModal || $[28] !== setOpenConfirmationModal) {
    t17 = openConfirmationModal && <Confirmation title="Delete Goal" cancelHandler={() => setOpenConfirmationModal(false)} confirmHandler={handleDelete} actionButtonText="Delete" variant="DANGER" message="Are you sure that you want to remove this goal?" />;
    $[26] = handleDelete;
    $[27] = openConfirmationModal;
    $[28] = setOpenConfirmationModal;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  return <div className="flex flex-col space-y-2">{t1}<div className="flex items-center space-x-2">{t3}{t8}<select title="Goal Type" value={t9} className="relative rounded-md border-2 border-ds-border-standard bg-transparent p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" onChange={t10}><option disabled={selectedTypes.includes(FactiiiGoalType.DAILY)} value={FactiiiGoalType.DAILY}>Daily</option><option disabled={selectedTypes.includes(FactiiiGoalType.WEEKLY)} value={FactiiiGoalType.WEEKLY}>Weekly</option><option disabled={selectedTypes.includes(FactiiiGoalType.MONTHLY)} value={FactiiiGoalType.MONTHLY}>Monthly</option>{t12}</select>{t16}</div>{t17}</div>;
};
export default GoalItem;
function _temp(g_0) {
  return g_0.type;
}
function _temp2(goal_0) {
  const integerRequired = Array.isArray(goal_0.factiiiRequirements) && goal_0.factiiiRequirements.includes(FactiiiRequirement.INTEGER_REQUIRED);
  if (goal_0.data.length == 0) {
    return {
      isValid: false,
      error: "Field can't be empty"
    };
  }
  if (integerRequired) {
    const isValid = /^-?[0-9]+$/.test(goal_0.data);
    return {
      isValid,
      error: isValid ? "" : `${goal_0.name} goal must have a integer value.`
    };
  }
  return {
    isValid: true,
    error: ""
  };
}